// theme.liquid とセクション双方から読み込まれても二重定義しない
(() => {
if (customElements.get('carousel-slider')) return;

class BlogCarousel extends HTMLElement {
  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeCarousel(), { once: true });
    } else {
      this.initializeCarousel();
    }
  }

  initializeCarousel() {
    this.controller?.abort();
    this.scroller = this.querySelector('.scroller');
    this.progressBar = this.querySelector('progress');

    if (!this.scroller || !this.progressBar) {
      console.error('BlogCarousel: Required elements not found');
      return;
    }

    this.items = [...this.scroller.children];
    if (this.items.length === 0) {
      console.error('BlogCarousel: No items found');
      return;
    }

    this.controller = new AbortController();
    const { signal } = this.controller;

    // デバウンス処理を含むスクロールイベント
    const debouncedUpdate = this.debounce(this.updateCarouselState.bind(this));

    this.scroller.addEventListener('scroll', debouncedUpdate, { signal });
    window.addEventListener('resize', debouncedUpdate, { signal });

    // アクセシビリティ属性の設定
    this.setAttributes();
    
    // 初期状態の更新
    debouncedUpdate();
  }

  setAttributes() {
    this.setAttribute('aria-roledescription', 'カルーセル');

    this.items.forEach((item, index) => {
      item.setAttribute('role', 'group');
      item.setAttribute('aria-roledescription', 'スライド');
      item.setAttribute('aria-label', `${index + 1} of ${this.items.length}`);
    });
  }

  scrollByItem(direction) {
    const scroller = this.querySelector('.scroller');
    const first = scroller?.firstElementChild;
    if (!scroller || !first) return;
    const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
    const slideWidth = first.getBoundingClientRect().width + gap || scroller.clientWidth;
    const maxLeft = scroller.scrollWidth - scroller.clientWidth;
    scroller.scrollLeft = Math.min(Math.max(scroller.scrollLeft + direction * slideWidth, 0), maxLeft);
  }

  updateArrowPosition() {
    const image = this.querySelector('.text-img-insta-size');
    if (image) this.style.setProperty('--carousel-arrow-top', `${image.offsetHeight / 2}px`);
  }

  updateCarouselState() {
    if (!this.scroller || !this.progressBar) return;

    this.updateArrowPosition();
    this.updateProgressBar();
    this.updateInertAttribute();
  }

  updateProgressBar() {
    if (!this.scroller || !this.progressBar) return;

    const scrollLeft = this.scroller.scrollLeft;
    const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
    
    // スクロール不可能な場合は0%
    if (maxScroll <= 0) {
      this.progressBar.value = 0;
      return;
    }

    const scrollPercentage = (scrollLeft / maxScroll) * 100;
    const clampedPercentage = Math.min(Math.max(scrollPercentage, 0), 100);
    this.progressBar.style.setProperty('--progress-position', `${clampedPercentage}%`);
  }

  updateInertAttribute() {
    if (!this.scroller) return;

    const scrollerRect = this.scroller.getBoundingClientRect();

    this.items.forEach((item) => {
      const itemRect = item.getBoundingClientRect();
      const isVisible = 
        itemRect.left < scrollerRect.right && 
        itemRect.right > scrollerRect.left;
      
      // inert属性でスクリーンリーダーの対象外にする
      item.inert = !isVisible;
    });
  }

  debounce(callback) {
    let timeout;
    return (...args) => {
      if (timeout !== undefined) cancelAnimationFrame(timeout);
      timeout = requestAnimationFrame(() => callback.apply(this, args));
    };
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

// カスタム要素として登録
if (!customElements.get('carousel-slider')) customElements.define('carousel-slider', BlogCarousel);

// 矢印クリックはdocumentのキャプチャ段階で1回だけ受ける。
// 要素の初期化状態・再描画・iframe等の子要素のイベント停止に依存しない。
if (!window.__blogCarouselArrowsBound) {
  window.__blogCarouselArrowsBound = true;
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-carousel-prev], [data-carousel-next]');
    const carousel = button?.closest('carousel-slider');
    if (!carousel || typeof carousel.scrollByItem !== 'function') return;
    event.preventDefault();
    event.stopPropagation();
    carousel.scrollByItem(button.hasAttribute('data-carousel-prev') ? -1 : 1);
  }, true);
}

// テーマエディタでのセクション再描画・ブロック選択時にも再初期化する
(() => {
  const reinit = (event) => {
    const root = event.target instanceof Element ? event.target : document;
    root.querySelectorAll('carousel-slider').forEach((el) => {
      if (typeof el.initializeCarousel === 'function') el.initializeCarousel();
    });
  };
  ['shopify:section:load', 'shopify:section:reorder', 'shopify:block:select', 'shopify:block:deselect']
    .forEach((name) => document.addEventListener(name, reinit));
})();
})();